"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { popActiveGuide, pushActiveGuide } from "@/lib/guides/helpIntent";
import type { GuideKey } from "@/lib/guides/types";

type ActiveGuideContextValue = {
  stack: readonly GuideKey[];
  register: (guideKey: GuideKey) => () => void;
};

const ActiveGuideContext = createContext<ActiveGuideContextValue>({
  stack: [],
  register: () => () => undefined,
});

export function ActiveGuideProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<GuideKey[]>([]);
  const register = useCallback((guideKey: GuideKey) => {
    setStack((current) => pushActiveGuide(current, guideKey));
    return () => {
      setStack((current) => popActiveGuide(current, guideKey));
    };
  }, []);
  const value = useMemo(() => ({ stack, register }), [stack, register]);
  return <ActiveGuideContext.Provider value={value}>{children}</ActiveGuideContext.Provider>;
}

export function useActiveGuideStack(): readonly GuideKey[] {
  return useContext(ActiveGuideContext).stack;
}

export function useRegisterActiveGuide(guideKey: GuideKey, when: boolean): void {
  const { register } = useContext(ActiveGuideContext);
  useEffect(() => {
    if (!when) {
      return;
    }
    return register(guideKey);
  }, [guideKey, register, when]);
}
